import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  InputAdornment
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { twoFactorAPI } from '../../services/api';
import { TwoFactorSetupResponse } from '../../types/api';

interface TwoFactorFinishSetupProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  setupData: TwoFactorSetupResponse; // Umesto samo secret-a
}

export const TwoFactorFinishSetup: React.FC<TwoFactorFinishSetupProps> = ({
  open,
  onClose,
  onSuccess,
  setupData
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFinish = async () => {
    if (code.length !== 6) {
      setError('Molimo unesite 6-cifreni kod');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await twoFactorAPI.enable({
        secret: setupData.secret,
        totp_code: code,
        backup_codes: setupData.backup_codes || []
      });

      if (response.enabled) {
        onSuccess();
        handleClose();
      } else {
        setError(response.message || 'Neispravna kod. Molimo pokušajte ponovo.');
      }
    } catch (err: any) {
      setError('Neuspešno aktiviranje 2FA. Molimo pokušajte ponovo.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setError(null);
    onClose();
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replaceAll(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && code.length === 6) {
      handleFinish();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon color="primary" />
          Završi Podešavanje 2FA
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Da biste završili podešavanje 2FA, unesite kod koji se prikazuje u vašoj aplikaciji za autentifikaciju:
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Kod za Potvrdu"
          value={code}
          onChange={handleCodeChange}
          onKeyPress={handleKeyPress}
          placeholder="000000"
          inputProps={{
            maxLength: 6,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              letterSpacing: '0.5rem'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SecurityIcon color="action" />
              </InputAdornment>
            ),
          }}
          helperText="Unesite 6-cifreni kod iz aplikacije za autentifikaciju"
          disabled={loading}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Otkaži
        </Button>
        <Button
          variant="contained"
          onClick={handleFinish}
          disabled={code.length !== 6 || loading}
        >
          {loading ? 'Aktiviram...' : 'Završi Podešavanje'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
